import { useState, useEffect, useMemo, useCallback } from 'react';
import { ticketsAPI, calendarAPI, hrAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { priorityBar, priorityColors, statusColors, statusLabels, getInitials } from '@/lib/utils';
import { formatMst, MST_LABEL } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2, MapPin, Plus, Trash2, Users, Plane } from 'lucide-react';
import EventDialog from './EventDialog';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PTO_LABELS = { vacation: 'Vacation', sick: 'Sick Leave', personal: 'Personal Day', unpaid: 'Unpaid Leave' };
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MST_OFFSET_MIN = 7 * 60;

/**
 * Return the start-of-day key in MST for a given UTC ISO string. We bucket
 * tickets / events by their MST calendar day so the calendar reflects
 * "MST always" (per spec) regardless of the viewer's browser timezone.
 */
function mstDayKey(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  const mst = new Date(d.getTime() - MST_OFFSET_MIN * 60_000);
  return `${mst.getUTCFullYear()}-${String(mst.getUTCMonth() + 1).padStart(2, '0')}-${String(mst.getUTCDate()).padStart(2, '0')}`;
}

/** Build a yyyy-mm-dd key for a Date object treated as a calendar-grid date. */
function calendarDayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Render just the time portion (e.g. "2:30 PM") of an ISO instant in MST. */
function mstTimeOnly(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mst = new Date(d.getTime() - MST_OFFSET_MIN * 60_000);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(mst);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarView({ onSelectTicket }) {
  const { user, isAgent } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(today);
  const [showEventDialog, setShowEventDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const start = new Date(cursor);
      start.setDate(start.getDate() - 7);
      const end = new Date(cursor);
      end.setMonth(end.getMonth() + 1);
      end.setDate(end.getDate() + 14);
      const params = { start: start.toISOString(), end: end.toISOString() };
      // Pull scheduled tickets, all tickets (for created/due), events, and approved team time off in parallel
      const [scheduled, allTickets, ev, pto] = await Promise.all([
        ticketsAPI.getCalendar(params).catch(() => ({ data: [] })),
        ticketsAPI.list({ include_closed: true }).catch(() => ({ data: [] })),
        calendarAPI.listEvents(params).catch(() => ({ data: [] })),
        hrAPI.teamPtoCalendar({
          start: start.toISOString().slice(0, 10),
          end: end.toISOString().slice(0, 10),
        }).catch(() => ({ data: [] })),
      ]);
      // Merge unique tickets (scheduled + all)
      const map = new Map();
      for (const t of scheduled.data || []) map.set(t.id, t);
      for (const t of allTickets.data || []) if (!map.has(t.id)) map.set(t.id, t);
      setTickets(Array.from(map.values()));
      setEvents(ev.data || []);
      setTimeOff(pto.data || []);
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    load();
  }, [load]);

  // Build 6-week grid (42 days), starting from the Sunday on/before the 1st
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startWeekDay = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startWeekDay);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [cursor]);

  const ticketsByDay = useMemo(() => {
    const m = new Map();
    const push = (iso, item) => {
      const k = mstDayKey(iso);
      if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(item);
    };
    for (const t of tickets) {
      // Bucket by MST calendar-day — same instant, but the day boundary is MST.
      if (t.scheduled_at) {
        push(t.scheduled_at, { kind: 'ticket', subKind: 'scheduled', when: t.scheduled_at, ...t });
      }
      if (t.created_at) {
        push(t.created_at, { kind: 'ticket', subKind: 'created', when: t.created_at, ...t });
      }
      if (t.sla_due_at && !['resolved', 'closed'].includes(t.status)) {
        push(t.sla_due_at, { kind: 'ticket', subKind: 'due', when: t.sla_due_at, ...t });
      }
    }
    return m;
  }, [tickets]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      if (!e.start_at) continue;
      const k = mstDayKey(e.start_at);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push({ kind: 'event', when: e.start_at, ...e });
    }
    return m;
  }, [events]);

  // Approved time off, expanded across each weekday (Mon–Fri) of its range.
  // PTO dates are plain YYYY-MM-DD strings — no timezone conversion needed.
  const timeOffByDay = useMemo(() => {
    const m = new Map();
    for (const p of timeOff) {
      if (!p.start_date || !p.end_date) continue;
      const s = new Date(`${p.start_date}T00:00:00`);
      const e = new Date(`${p.end_date}T00:00:00`);
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay();
        if (wd === 0 || wd === 6) continue;
        const k = calendarDayKey(d);
        if (!m.has(k)) m.set(k, []);
        m.get(k).push({ kind: 'timeoff', when: `${k}T00:00:00`, ...p });
      }
    }
    return m;
  }, [timeOff]);

  const getItemsForDay = (d) => {
    const k = calendarDayKey(d);
    return [
      ...(timeOffByDay.get(k) || []),
      ...[...(ticketsByDay.get(k) || []), ...(eventsByDay.get(k) || [])].sort(
        (a, b) => new Date(a.when) - new Date(b.when)
      ),
    ];
  };

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    setCursor(startOfMonth(new Date()));
    setSelectedDate(new Date());
  };

  const selectedDayItems = useMemo(() => {
    return getItemsForDay(selectedDate);
  }, [selectedDate, ticketsByDay, eventsByDay, timeOffByDay]);

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this calendar event?')) return;
    try {
      await calendarAPI.deleteEvent(id);
      await load();
    } catch (err) {
      alert('Failed to delete event');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="calendar-view">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-7 h-7 text-cyan-500" />
              Shared Calendar
            </h1>
            <p className="text-sm md:text-base text-slate-500 mt-1">All scheduled tickets visible to your team — times are shown in <strong>MST</strong></p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={goPrev} className="rounded-full h-10 w-10 p-0" aria-label="Previous month" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="px-4 h-10 inline-flex items-center text-base font-semibold text-slate-800 min-w-[160px] justify-center" data-testid="cal-month">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <Button variant="outline" onClick={goNext} className="rounded-full h-10 w-10 p-0" aria-label="Next month" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" onClick={goToday} className="rounded-full h-10" data-testid="cal-today">Today</Button>
            {isAgent && (
              <Button onClick={() => setShowEventDialog(true)} className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white h-10 px-5 shadow-md shadow-cyan-500/30" data-testid="cal-add-event-btn">
                <Plus className="w-4 h-4 mr-1.5" />Add Event
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading calendar…
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar grid */}
          <Card className="lg:col-span-2 bg-white border-slate-200">
            <CardContent className="p-3 md:p-4">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="text-center text-xs font-semibold text-slate-500 uppercase py-2">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  const isCurMonth = d.getMonth() === cursor.getMonth();
                  const isToday = isSameDay(d, today);
                  const isSelected = isSameDay(d, selectedDate);
                  const dayItems = getItemsForDay(d);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedDate(d)}
                      className={`group min-h-[88px] md:min-h-[110px] text-left rounded-xl p-2 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-50/60'
                          : isCurMonth
                          ? 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-slate-50'
                          : 'border-slate-100 bg-slate-50/40 text-slate-400 hover:border-slate-200'
                      }`}
                      data-testid={`cal-cell-${d.toISOString().slice(0, 10)}`}
                      aria-label={`${d.toDateString()}, ${dayItems.length} items`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isToday ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-500 text-white' : ''}`}>{d.getDate()}</span>
                        {dayItems.length > 0 && (
                          <span className="text-[10px] font-semibold bg-slate-900 text-white rounded-full px-1.5 py-0.5">{dayItems.length}</span>
                        )}
                      </div>
                      <div className="mt-1 space-y-1">
                        {dayItems.slice(0, 3).map((it) => (
                          <div key={`${it.kind}-${it.id}`} className="flex items-center gap-1 truncate">
                            {it.kind === 'timeoff' ? (
                              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-violet-500" aria-hidden="true" />
                            ) : it.kind === 'event' ? (
                              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-cyan-500" aria-hidden="true" />
                            ) : (
                              <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${priorityBar[it.priority] || 'bg-slate-400'}`} aria-hidden="true" />
                            )}
                            <span className="text-[11px] text-slate-700 truncate">
                              {it.kind === 'timeoff' ? `${it.employee_name} · Off` : it.kind === 'event' ? it.title : it.subject}
                            </span>
                          </div>
                        ))}
                        {dayItems.length > 3 && (
                          <span className="text-[10px] text-slate-500">+{dayItems.length - 3} more</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Day details panel */}
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Selected day</p>
                <p className="text-lg font-bold text-slate-900">{selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              {selectedDayItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500 border border-dashed border-slate-200 rounded-xl">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nothing scheduled.</p>
                  {isAgent && (
                    <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => setShowEventDialog(true)} data-testid="cal-empty-add-event">
                      <Plus className="w-3.5 h-3.5 mr-1.5" />Add an event
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="space-y-2">
                  {selectedDayItems.map((it) => it.kind === 'timeoff' ? (
                    <li key={`timeoff-${it.id}`}>
                      <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3" data-testid={`cal-timeoff-${it.id}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-1 w-1 h-10 rounded-full bg-violet-500" aria-hidden="true" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <Badge className="bg-violet-500 text-white border-violet-500 text-[10px] uppercase tracking-wide">Time Off</Badge>
                              <span className="text-[11px] text-slate-500">{PTO_LABELS[it.type] || it.type}</span>
                            </div>
                            <h3 className="font-semibold text-slate-900 mt-1 truncate inline-flex items-center gap-1.5">
                              <Plane className="w-3.5 h-3.5 text-violet-500" />{it.employee_name}
                            </h3>
                            <p className="text-[12px] text-slate-600 mt-0.5">
                              {it.start_date === it.end_date
                                ? it.start_date
                                : `${it.start_date} → ${it.end_date}`} · {it.days} business day{it.days === 1 ? '' : 's'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  ) : it.kind === 'event' ? (
                    <li key={`event-${it.id}`}>
                      <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-3" data-testid={`cal-event-${it.id}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-1 w-1 h-10 rounded-full bg-cyan-500" aria-hidden="true" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <Badge className="bg-cyan-500 text-white border-cyan-500 text-[10px] uppercase tracking-wide">Event</Badge>
                              <span className="text-[11px] text-slate-500">
                                {mstTimeOnly(it.start_at)} {MST_LABEL}
                              </span>
                            </div>
                            <h3 className="font-semibold text-slate-900 mt-1 truncate">{it.title}</h3>
                            {it.location && (
                              <p className="text-[12px] text-slate-600 mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{it.location}</p>
                            )}
                            {(it.attendees?.length > 0) && (
                              <p className="text-[12px] text-slate-600 mt-1 inline-flex items-center gap-1">
                                <Users className="w-3 h-3 text-slate-400" />
                                {it.attendees.length} attendee{it.attendees.length !== 1 ? 's' : ''}
                              </p>
                            )}
                            {it.description && (
                              <p className="text-[12px] text-slate-700 mt-1 line-clamp-2">{it.description}</p>
                            )}
                            {isAgent && (
                              <div className="flex justify-end mt-2">
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => handleDeleteEvent(it.id)} data-testid={`cal-delete-event-${it.id}`} aria-label="Delete event">
                                  <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ) : (
                    <li key={`ticket-${it.id}-${it.subKind || 'sched'}`}>
                      <button
                        type="button"
                        onClick={() => onSelectTicket?.(it)}
                        className="w-full text-left rounded-xl border border-slate-200 hover:border-cyan-300 hover:bg-slate-50 p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                        data-testid={`cal-ticket-${it.id}-${it.subKind || 'scheduled'}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`mt-1 w-1 h-10 rounded-full ${priorityBar[it.priority] || 'bg-slate-300'}`} aria-hidden="true" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-[11px] text-slate-500">{it.ticket_number}</span>
                              <span className="text-[11px] text-slate-500">
                                {mstTimeOnly(it.when)} {MST_LABEL}
                              </span>
                            </div>
                            <h3 className="font-semibold text-slate-900 truncate">{it.subject}</h3>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {it.subKind === 'created' && (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Created</Badge>
                              )}
                              {it.subKind === 'due' && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">SLA Due</Badge>
                              )}
                              {it.subKind === 'scheduled' && (
                                <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 text-[10px]">Scheduled</Badge>
                              )}
                              <Badge className={`${statusColors[it.status] || statusColors.open} border text-[11px]`}>{statusLabels[it.status] || it.status}</Badge>
                              <Badge className={`${priorityColors[it.priority]} border text-[11px]`}>{it.priority}</Badge>
                              {it.location && (
                                <Badge variant="outline" className="text-[11px] border-slate-200 text-slate-600">
                                  <MapPin className="w-3 h-3 mr-1" />{it.location}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-2">
                              <span className="text-[12px] text-slate-700 truncate"><strong>{it.customer_name || '—'}</strong></span>
                              {it.assigned_agent_name ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <Avatar className="w-5 h-5 border border-slate-200">
                                    <AvatarFallback className="bg-cyan-100 text-cyan-700 text-[10px]">{getInitials(it.assigned_agent_name)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-[12px] text-slate-700 truncate max-w-[120px]">{it.assigned_agent_name}</span>
                                </span>
                              ) : (
                                <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Unassigned</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-500" />Event</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" />Time Off</span>
          <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${priorityBar.urgent}`} />Urgent / Critical</span>
          <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${priorityBar.high}`} />High</span>
          <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${priorityBar.medium}`} />Medium</span>
          <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${priorityBar.low}`} />Low</span>
        </div>
      </div>

      {isAgent && (
        <EventDialog
          open={showEventDialog}
          onOpenChange={setShowEventDialog}
          initialDate={selectedDate}
          onSaved={load}
        />
      )}
    </div>
  );
}
